import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicHeader from "../../components/PublicHeader";
import PublicFooter from "../../components/PublicFooter";
import VerifiedIcon from "../../assets/icon/verified-icons.png";
import VerifiedIconGray from "../../assets/icon/verified-Icon-gray.png";
import { FiArrowLeft } from "react-icons/fi";
import apiService from "../../actions/apiService";

const PublicListingDetails = () => {
  const { id } = useParams();
  const itemId = id || "";

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [sellerStats, setSellerStats] = useState({
    totalSales: 0,
    totalSoldValue: 0,
    sellerRating: 0,
  });
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const result = await apiService.get(`/items/public/${itemId}`);
        if (!active) return;
        if (result.success) {
          setItem(result.data);
          setImageError(false); // Reset image error when new item loads
          // Fetch seller stats if seller id present
          const sellerId = result?.data?.item?.seller?.id;
          if (sellerId) {
            try {
              const statsRes = await apiService.get(
                "/transactions/public/seller-stats",
                {
                  seller_id: sellerId,
                }
              );
              if (statsRes?.success && statsRes.data) {
                setSellerStats({
                  totalSales: statsRes.data.totalSales || 0,
                  totalSoldValue: statsRes.data.totalSoldValue || 0,
                  sellerRating:
                    typeof statsRes.data.sellerRating === "number"
                      ? statsRes.data.sellerRating
                      : 0,
                });
              }
            } catch (_) {
              /* ignore stats errors */
            }
          }
        } else {
          setError(result.error || "Failed to load item");
        }
      } catch (err) {
        setError("Failed to load item details");
        console.error("Error loading item:", err);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [itemId]);

  // const handleContactSeller = () => {
  //   if (!item?.item?.seller?.email) return;
  //   const mailtoLink = `mailto:${item?.item?.seller?.email}`;
  //   window.open(mailtoLink, "_self");
  // };

  const imageBaseUrl = process.env.REACT_APP_IMAGE_URL || "";
  const imageUrls = (item?.item?.images || []).map(
    (img) => `${imageBaseUrl}${img.file_url}`
  );

  const mainImageSrc = imageUrls[selectedImageIdx] || "";

  if (loading) {
    return (
      <div className="min-h-screen bg-esbgColor flex flex-col">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-esblue"></div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (error || !item?.item) {
    return (
      <div className="min-h-screen bg-esbgColor flex flex-col">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center px-5 md:px-16 2xl:px-8 py-8">
          <div className="text-center">
            <p className="text-esdarkGray text-lg mb-4">
              {error || "Item not found"}
            </p>
            <Link
              to="/"
              className="text-esblue inline-flex items-center gap-2 text-[14px] font-[600]"
            >
              <FiArrowLeft className="w-3.5 h-3.5" />
              Back to home
            </Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-esbgColor flex flex-col">
      <PublicHeader />
      <main className="flex-1">
        <div className="max-w-[1512px] mx-auto px-5 md:px-16 2xl:px-8 py-8">
          <div className="mb-6 font-[600]">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-esblue text-[14px] font-[600]"
            >
              <FiArrowLeft className="w-3.5 h-3.5" /> Back to search results
            </Link>
          </div>
          <h1 className="text-page-title md:text-page-title-d text-[#1E1E1E] font-rubik">
            {item?.item?.title || "N/A"}
          </h1>
          <p className="text-[12px] md:text-[14px] opacity-70 text-[#1E1E1E] mt-1">
            Main information and short description of the listing, keywords,
            year
          </p>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-5 items-start">
            {/* Left column */}
            <div className="space-y-5">
              {/* Gallery */}
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                <div className="mb-4 h-[292px] w-full bg-gray-100 rounded-[12px] overflow-hidden relative">
                  {mainImageSrc ? (
                    <img
                      src={mainImageSrc}
                      alt="listing"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100" />
                  )}
                  <div className="absolute left-3 top-3">
                    <div className="justify-start items-center flex gap-1 font-medium w-fit px-2.5 py-1 rounded-full bg-[#E0E0E0] text-[#575757]">
                      Listed
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {imageUrls.map((url, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedImageIdx(idx)}
                      className={`h-[104px] cursor-pointer w-full rounded-[12px] overflow-hidden bg-white border ${idx === selectedImageIdx
                        ? "border-esblue"
                        : "border-gray-200"
                        }`}
                    >
                      <img
                        src={url}
                        alt={`thumb-${idx}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                  {Array.from({
                    length: Math.max(0, 3 - imageUrls.length),
                  }).map((_, i) => (
                    <div
                      key={`ph-${i}`}
                      className="h-[104px] w-full rounded-[12px] overflow-hidden bg-gray-100"
                    ></div>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik">
                  Description
                </h3>
                <p className="text-[#000025] font-[400] text-[16px] opacity-50 mt-4">
                  {item?.item?.description ? item?.item?.description : "-"}
                </p>
              </div>

              {/* Specifications */}
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik">
                  Specifications
                </h3>
                <div className="grid mt-4 grid-cols-1 gap-x-10 gap-y-3">
                  {item?.item?.plate_number && (
                    <div className="flex items-center justify-between">
                      <span className="text-[16px] font-[400] text-[#000025] opacity-50">
                        Plate number
                      </span>
                      <span className="text-[16px] font-[600] text-[#000025] opacity-50">
                        {item?.item?.plate_number}
                      </span>
                    </div>
                  )}
                  {Object.entries(item?.item?.custom_properties || {}).map(
                    ([k, v], i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between"
                      >
                        <span className="text-[16px] font-[400] text-[#000025] opacity-50">
                          {k}
                        </span>
                        <span className="text-[16px] font-[600] text-[#000025] opacity-50">
                          {v}
                        </span>
                      </div>
                    )
                  )}
                  {Object.entries(item?.item?.custom_properties || {})
                    .length === 0 &&
                    (!item?.item?.plate_number ||
                      item?.item?.plate_number === null) && (
                      <div className="flex items-center justify-between">
                        <span className="text-[16px] font-[400] text-[#000025] opacity-50">
                          N/A
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-5">
              {/* Listing details */}
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik">
                  Listing details
                </h3>
                <p className="text-[#000025] font-[400] text-[16px] opacity-50 mt-4">
                  Secure transaction through escrow
                </p>
                <div className="mt-8">
                  <div className="flex items-center justify-between text-[14px]">
                    <span className="text-[16px] text-[#1E1E1E] font-[600]">
                      Item price
                    </span>
                    <span className="text-[18px] text-[#000025] font-[700]">
                      {item?.item?.pricing?.basePrice?.toFixed(2) ||
                        item?.item?.price?.toFixed(2) ||
                        "0.00"}{" "}
                      €
                    </span>
                  </div>
                  <div className="flex items-center mt-2 justify-between text-[14px]">
                    <span className="text-[16px] opacity-50 text-[#1E1E1E] font-[400]">
                      Service fee (
                      {item?.item?.pricing?.buyerFeePercentage
                        ? Number(item.item.pricing.buyerFeePercentage) % 1 === 0
                          ? item.item.pricing.buyerFeePercentage
                          : Number(
                            item.item.pricing.buyerFeePercentage
                          ).toFixed(2)
                        : 1}
                      %)
                    </span>
                    <span className="text-[16px] opacity-50 text-[#000025] font-[400]">
                      {item?.item?.pricing?.platformFee?.toFixed(2) || "0.00"} €
                    </span>
                  </div>
                  <div className="flex items-center mt-8 justify-between">
                    <span className="text-[16px] text-[#1E1E1E] font-[600]">
                      Total
                    </span>
                    <span className="text-big-price md:text-big-price-d font-[700] text-esblue">
                      {item?.item?.pricing?.finalPrice?.toFixed(2) ||
                        item?.item?.price?.toFixed(2) ||
                        "0.00"}{" "}
                      €
                    </span>
                  </div>
                </div>

                {/* Additional services */}
                {/* <div className="mt-8">
                  <h4 className="text-[20px] md:text-[24px] font-[600] text-[#1E1E1E] mb-4">
                    Additionnal services
                  </h4>
                  <div className="mb-4 rounded-[8px] bg-white">
                    <p className="text-[14px] text-[#1E1E1E] opacity-50">
                      Amount of the additional services will be deducted from
                      the amount held in escrow.
                    </p>
                  </div>
                  <div className="bg-[#E5F2FF] border border-[#00000026] rounded-[8px] p-2 px-4 flex items-center gap-3">
                    <div className="w-5 h-5 rounded border-2 bg-esblue border-esblue flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h5 className="text-[16px] font-[600] text-[#1E1E1E] mb-1">
                        ProtectPay product authentification
                      </h5>
                      <p className="text-[16px] text-[#000025] opacity-50">
                        Professional authentication service with certificate
                      </p>
                    </div>
                    <div className="text-[#575757] border border-[#00000026]  px-[10px] py-[4px] text-[14px] font-[600] rounded-[50px] ">
                      + {item?.item?.authenticationFee} €
                    </div>
                  </div>
                </div> */}

                {/* Login to send offer button */}
                <Link
                  to="/login"
                  className="mt-8 flex-1 md:flex-none bg-blue-gradient text-white w-full text-[14px] py-[12.5px] rounded-[8px] inline-flex items-center font-[600] justify-center gap-4"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M13 1H14.2C15.8802 1 16.7202 1 17.362 1.32698C17.9265 1.6146 18.3854 2.07354 18.673 2.63803C19 3.27976 19 4.11985 19 5.8V14.2C19 15.8802 19 16.7202 18.673 17.362C18.3854 17.9265 17.9265 18.3854 17.362 18.673C16.7202 19 15.8802 19 14.2 19H13M8 5L13 10M13 10L8 15M13 10L1 10"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>Login to send an offer</span>
                </Link>
              </div>

              {/* Seller information */}
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik mb-8">
                  Seller information
                </h3>
                <div className="flex items-center gap-4">
                  {item?.item?.seller?.profile_image_url && !imageError ? (
                    <img
                      src={
                        process.env.REACT_APP_IMAGE_URL +
                        item.item.seller.profile_image_url
                      }
                      alt="avatar"
                      className="w-[65px] h-[65px] rounded-full object-cover"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <span className="w-[65px] h-[65px] rounded-full bg-gray-200 text-gray-600 text-xl font-semibold flex items-center justify-center">
                      {item?.item?.seller?.firstName
                        ?.charAt(0)
                        ?.toUpperCase() || ""}
                      {item?.item?.seller?.lastName?.charAt(0)?.toUpperCase() ||
                        ""}
                    </span>
                  )}
                  <div>
                    <div className="text-[18px] opacity-75 font-[400] text-[#1E1E1E]">
                      {item?.item?.seller?.name ||
                        `${item?.item?.seller?.firstName || ""} ${item?.item?.seller?.lastName || ""
                          }`.trim() ||
                        "N/A"}
                    </div>
                    <div className="text-[12px] opacity-50 text-[#1E1E1E]">
                      Joined August 2025
                    </div>
                  </div>
                  <span className="bg-[#EAF8EE] flex justify-center gap-1 items-center text-[#16653F] text-[14px] font-[600] px-[10px] py-1 rounded-full">
                    <img
                      src={VerifiedIcon}
                      alt={`thumb-verified`}
                      className="w-[12px] h-[12px] object-cover"
                    />
                    <span>Verified</span>
                  </span>
                </div>
                <div className="mt-8 text-[14px]">
                  <div className="flex blur items-center gap-4">
                    <span className="text-[16px] font-[400] text-[#000025]">
                      demo@example.com
                    </span>
                  </div>
                  <div className="flex blur mt-4 items-center gap-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <g opacity="0.5">
                        <path
                          d="M8.38028 8.85335C9.07627 10.303 10.0251 11.6616 11.2266 12.8632C12.4282 14.0648 13.7869 15.0136 15.2365 15.7096C15.3612 15.7694 15.4235 15.7994 15.5024 15.8224C15.7828 15.9041 16.127 15.8454 16.3644 15.6754C16.4313 15.6275 16.4884 15.5704 16.6027 15.4561C16.9523 15.1064 17.1271 14.9316 17.3029 14.8174C17.9658 14.3864 18.8204 14.3864 19.4833 14.8174C19.6591 14.9316 19.8339 15.1064 20.1835 15.4561L20.3783 15.6509C20.9098 16.1824 21.1755 16.4481 21.3198 16.7335C21.6069 17.301 21.6069 17.9713 21.3198 18.5389C21.1755 18.8242 20.9098 19.09 20.3783 19.6214L20.2207 19.779C19.6911 20.3087 19.4263 20.5735 19.0662 20.7757C18.6667 21.0001 18.0462 21.1615 17.588 21.1601C17.1751 21.1589 16.8928 21.0788 16.3284 20.9186C13.295 20.0576 10.4326 18.4332 8.04466 16.0452C5.65668 13.6572 4.03221 10.7948 3.17124 7.76144C3.01103 7.19699 2.93092 6.91477 2.9297 6.50182C2.92833 6.0436 3.08969 5.42311 3.31411 5.0236C3.51636 4.66357 3.78117 4.39876 4.3108 3.86913L4.46843 3.7115C4.99987 3.18006 5.2656 2.91433 5.55098 2.76999C6.11854 2.48292 6.7888 2.48292 7.35636 2.76999C7.64174 2.91433 7.90747 3.18006 8.43891 3.7115L8.63378 3.90637C8.98338 4.25597 9.15819 4.43078 9.27247 4.60655C9.70347 5.26945 9.70347 6.12403 9.27247 6.78692C9.15819 6.96269 8.98338 7.1375 8.63378 7.4871C8.51947 7.60142 8.46231 7.65857 8.41447 7.72538C8.24446 7.96281 8.18576 8.30707 8.26748 8.58743C8.29048 8.66632 8.32041 8.72866 8.38028 8.85335Z"
                          stroke="#1E1E1E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    </svg>
                    <span className="text-[16px] font-[400] text-[#000025]">
                      +33 6 12 34 56 78
                    </span>
                  </div>
                </div>
                <div className="mt-8 flex justify-between flex-wrap text-center">
                  <div className="text-left">
                    <div className="text-[12px] text-[#1E1E1E] font-[500] opacity-50">
                      Total sales
                    </div>
                    <div className="text-page-subtitle-d font-[700]">
                      {sellerStats.totalSales ?? 0}
                    </div>
                  </div>
                  <div className="text-left">
                    <div className="text-[12px] text-[#1E1E1E] font-[500] opacity-50">
                      Total sold
                    </div>
                    <div className="text-page-subtitle-d font-[700]">
                      {sellerStats.totalSoldValue?.toFixed(2) ?? 0} €
                    </div>
                  </div>
                </div>
                {/* <button
                  onClick={handleContactSeller}
                  className="mt-8 w-full bg-eslightBlue text-esblue py-3 rounded-[8px] text-[14px] font-[600]"
                >
                  Contact seller
                </button> */}
              </div>

              {/* Security and protection */}
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                <h3 className="text-big-price md:text-big-price-d font-[500] text-[#1E1E1E] font-rubik mb-4">
                  Security and protection
                </h3>
                <ul className="space-y-4 text-[14px]">
                  <li className="flex items-center gap-3">
                    <span className="">
                      <img
                        src={VerifiedIconGray}
                        alt={`thumb-verified`}
                        className="object-contain"
                      />
                    </span>
                    <div className="space-y-1">
                      <p className="text-[#000025] font-[600] text-[16px]">
                        Escrow protection
                      </p>
                      <p className="text-[#000025] font-[400] opacity-50 text-[16px]">
                        Payment held securely until item received
                      </p>
                    </div>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="">
                      <img
                        src={VerifiedIconGray}
                        alt={`thumb-verified`}
                        className="object-contain"
                      />
                    </span>
                    <div className="space-y-1">
                      <p className="text-[#000025] font-[600] text-[16px]">
                        Seller verified
                      </p>
                      <p className="text-[#000025] font-[400] opacity-50 text-[16px]">
                        Identity and reputation confirmed
                      </p>
                    </div>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="">
                      <img
                        src={VerifiedIconGray}
                        alt={`thumb-verified`}
                        className="object-contain"
                      />
                    </span>
                    <div className="space-y-1">
                      <p className="text-[#000025] font-[600] text-[16px]">
                        Authenticity verified (optional service)
                      </p>
                      <p className="text-[#000025] font-[400] opacity-50 text-[16px]">
                        Item authenticity guaranteed by experts
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
};

export default PublicListingDetails;
